/**
 * Report query hooks (Phase 3, §3.3 / §6.3 / §6.6) — read-only.
 *
 * Every report endpoint is GET-only (no CSRF) and returns figures keyed by
 * `bucket_id`; the pages join name/icon/colour/group from the single
 * `['buckets']` cache. Range-keyed reports share a cache entry when the
 * normalised `{from,to}` object is equal (§6.6).
 *
 * The pure month helpers (`currentMonth`, `addMonths`, `trailingRange`) live
 * here so the Budget month switcher and the Berichte `RangeSelect` derive their
 * ranges from one portable, unit-tested source (mirrors the backend
 * `reporting.py` seam on the client).
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from '../api.ts';
import { qk, type MonthRange } from '../queryKeys.ts';
import type {
  BucketTrendsOut,
  CashflowOut,
  CategoryBreakdownOut,
  MonthStr,
  MonthViewOut,
  NetWorthOut,
} from '../types.ts';

const STALE = 60 * 1000;

// ── Pure month helpers (client mirror of services/reporting.py) ─────────────

/** The current calendar month in Europe/Berlin, as `YYYY-MM`. */
export function currentMonth(): MonthStr {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(new Date());
  const y = parts.find((p) => p.type === 'year')?.value ?? '1970';
  const m = parts.find((p) => p.type === 'month')?.value ?? '01';
  return `${y}-${m}`;
}

/** Add `n` (possibly negative) whole months to a `YYYY-MM` key. */
export function addMonths(month: MonthStr, n: number): MonthStr {
  const [ys, ms] = month.split('-');
  const idx = Number(ys) * 12 + (Number(ms) - 1) + n;
  const y = Math.floor(idx / 12);
  const m = ((idx % 12) + 12) % 12;
  return `${String(y).padStart(4, '0')}-${String(m + 1).padStart(2, '0')}`;
}

/** The month before `month`. */
export function prevMonth(month: MonthStr): MonthStr {
  return addMonths(month, -1);
}

/** A trailing range of `months` ending at `to` (default: the current month). */
export function trailingRange(months = 12, to: MonthStr = currentMonth()): MonthRange {
  return { from: addMonths(to, -(months - 1)), to };
}

// ── Query hooks ─────────────────────────────────────────────────────────────

/** `GET /api/reports/month` — spent-vs-budget; the Budget page's primary source. */
export function useReportMonth(month: MonthStr): UseQueryResult<MonthViewOut> {
  return useQuery({
    queryKey: qk.reportMonth(month),
    queryFn: () => api.get<MonthViewOut>(`/api/reports/month?month=${month}`),
    staleTime: STALE,
  });
}

/** `GET /api/reports/categories` — single-month spend-by-bucket (donut). */
export function useReportCategories(month: MonthStr): UseQueryResult<CategoryBreakdownOut> {
  return useQuery({
    queryKey: qk.reportCategories(month),
    queryFn: () => api.get<CategoryBreakdownOut>(`/api/reports/categories?month=${month}`),
    staleTime: STALE,
  });
}

/** `GET /api/reports/cashflow` — dense monthly income/expense/net series. */
export function useReportCashflow(range: MonthRange): UseQueryResult<CashflowOut> {
  return useQuery({
    queryKey: qk.reportCashflow(range),
    queryFn: () => api.get<CashflowOut>(`/api/reports/cashflow?from=${range.from}&to=${range.to}`),
    staleTime: STALE,
  });
}

/** `GET /api/reports/bucket-trend` — per-bucket spend matrix. */
export function useReportBucketTrend(range: MonthRange): UseQueryResult<BucketTrendsOut> {
  return useQuery({
    queryKey: qk.reportBucketTrend(range),
    queryFn: () =>
      api.get<BucketTrendsOut>(`/api/reports/bucket-trend?from=${range.from}&to=${range.to}`),
    staleTime: STALE,
  });
}

/** `GET /api/reports/networth` — carry-forward CLBD net-worth series. */
export function useReportNetworth(range: MonthRange): UseQueryResult<NetWorthOut> {
  return useQuery({
    queryKey: qk.reportNetworth(range),
    queryFn: () =>
      api.get<NetWorthOut>(
        `/api/reports/networth?from=${range.from}&to=${range.to}&granularity=month`,
      ),
    staleTime: STALE,
  });
}
